"use client";

import { useTranslations } from "next-intl";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";

interface CTASectionProps {
  onScrollToPricing: () => void;
  onScrollToLeads: () => void;
  showFreeClass?: boolean;
}

export function CTASection({
  onScrollToPricing,
  onScrollToLeads,
  showFreeClass = true,
}: CTASectionProps) {
  const t = useTranslations("Home.cta");
  const ctaAnimation = useScrollAnimation({ threshold: 0.3 });

  return (
    <section className="py-24 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/30 to-black"></div>
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        <div
          ref={ctaAnimation.ref}
          className={`transition-all duration-1000 ease-out ${
            ctaAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white font-[family-name:var(--font-big-shoulders)] uppercase leading-tight">
            {t("title")}
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t("subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
            <button
              onClick={onScrollToPricing}
              className="bg-[#E91E63] hover:bg-[#D81B60] text-white text-lg font-bold px-8 py-4 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              {t("primaryButton")}
            </button>
            {showFreeClass && (
              <button
                onClick={onScrollToLeads}
                className="border-2 border-white/30 hover:border-white/50 hover:bg-white/10 text-white text-lg font-bold px-8 py-4 transition-all duration-300 hover:scale-105"
              >
                {t("secondaryButton")}
              </button>
            )}
          </div>

          {/* Trust indicators */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              <div className="space-y-2">
                <div className="text-2xl font-bold text-white">
                  {t("stats.students")}
                </div>
                <div className="text-sm text-gray-400">
                  {t("stats.studentsLabel")}
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-white">
                  {t("stats.championships")}
                </div>
                <div className="text-sm text-gray-400">
                  {t("stats.championshipsLabel")}
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-white">
                  {t("stats.experience")}
                </div>
                <div className="text-sm text-gray-400">
                  {t("stats.experienceLabel")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
